/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1672790400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jan 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "posthog",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Sending events to PostHog",
          "depth": 0,
          "id": "sending-events-to-posthog"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      em: "em",
      ul: "ul",
      li: "li",
      img: "img",
      code: "code",
      h3: "h3",
      strong: "strong"
    }, _provideComponents(), props.components), {EventSyncWarning} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["After selecting PostHog from our destinations catalog, you will be required to enter your PostHog ", _jsx(_components.a, {
          href: "https://posthog.com/docs/api/post-only-endpoints",
          children: _jsx(_components.em, {
            children: "Project API Key"
          })
        }), " and ", _jsx(_components.em, {
          children: "Public URL"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You can locate your Project API key within your PostHog account by navigating to ", _jsx(_components.em, {
            children: "your PostHog project"
          }), " > ", _jsx(_components.em, {
            children: "Project Settings"
          }), " > ", _jsx(_components.em, {
            children: "Project API key"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-posthog-setup.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["If you are using the cloud-hosted version of PostHog, your Public URL will be ", _jsx(_components.a, {
            href: "https://app.posthog.com/batch/",
            children: "https://app.posthog.com/batch/"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["If you are using a self-hosted version of PostHog, replace ", _jsx(_components.a, {
            href: "https://app.posthog.com/",
            children: "https://app.posthog.com/"
          }), " with the URL of your instance."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing both ", _jsx(_components.code, {
          children: "Objects"
        }), " and ", _jsx(_components.code, {
          children: "Events"
        }), " to PostHog."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following PostHog objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "People"
          }), " (", _jsx(_components.code, {
            children: "Persons"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports the following sync modes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), " - pushes new ", _jsx(_components.code, {
            children: "Person"
          }), " objects to PostHog and updates properties that change in your warehouse."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "Person"
        }), " records can be matched from your source to your PostHog workspace by Distinct ID (", _jsx(_components.code, {
          children: "distinct_id"
        }), "), which is a unique identifier\nin PostHog for each ", _jsx(_components.code, {
          children: "Person"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-posthog-idfieldmapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You may also sync columns from your source to PostHog's ", _jsx(_components.code, {
          children: "Person"
        }), " properties. PostHog doesn't provide information on properties set for ", _jsx(_components.code, {
          children: "People"
        }), " objects,\nso you will need to type the exact name of the property you would like to map to in PostHog. These property names must be spelled correctly and are case-sensitive."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-posthog-fieldmappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sending-events-to-posthog",
        children: "Sending events to PostHog"
      }), "\n", _jsx(_components.p, {
        children: "The PostHog integration also allows you to send events to PostHog with custom properties."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsxs(_components.p, {
        children: ["The four elements of any PostHog event are the ", _jsx(_components.em, {
          children: "Event Name"
        }), " (", _jsx(_components.strong, {
          children: "required"
        }), ", which can be a single string used to name all events, or pulled from your warehouse per event), a ", _jsx(_components.em, {
          children: "Timestamp"
        }), " (", _jsx(_components.strong, {
          children: "optional"
        }), ", in ISO 1806 format, or a Unix timestamp in ", _jsx(_components.strong, {
          children: "milliseconds"
        }), "), a ", _jsx(_components.strong, {
          children: "Distinct ID"
        }), " (", _jsx(_components.strong, {
          children: "required"
        }), ", the unique ID of the user corresponding to the event), and event properties."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-posthog-events.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can also map properties that will become event ", _jsx(_components.code, {
          children: "properties"
        }), " in PostHog, just as you would for object properties."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
